// Extracted from ClientGoogleFeedPreviewPage.js (Phase 2a split).
import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronRight, BarChart3 } from "lucide-react";
import { formatNumber, formatMetric, humanLabel, metricFormat } from "./formatters";
export function KpiTile({ label, value, formatted, testId }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-white p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
        {label}
      </div>
      <div className="font-display font-bold text-2xl tracking-tight text-zinc-900">
        {formatted}
      </div>
      <div className="text-[10px] font-mono text-zinc-400 mt-1">{value}</div>
    </div>
  );
}

/** A single breakdown rendered as a table. Click any column header to sort. */
export function BreakdownTable({ title, rows }) {
  const [sort, setSort] = useState({ key: null, dir: "desc" });

  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const sortedRows = useMemo(() => {
    if (!sort.key || safeRows.length === 0) return safeRows;
    const arr = [...safeRows];
    const mult = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * mult;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * mult;
    });
    return arr;
  }, [safeRows, sort]);

  if (safeRows.length === 0) {
    return (
      <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-500 italic">
        {title}: no rows returned
      </div>
    );
  }

  // Column order = keys of the first row. Try to put the dimension column first.
  const allKeys = Array.from(new Set(safeRows.flatMap((r) => Object.keys(r))));
  const numericKeys = ["clicks", "impressions", "ctr", "position", "cost",
                       "conversions", "sessions", "activeUsers", "views",
                       "estimatedMinutesWatched", "likes"];
  const dimKey = allKeys.find((k) => !numericKeys.includes(k)) || allKeys[0];
  const otherKeys = allKeys.filter((k) => k !== dimKey);

  const headerClick = (key) => {
    setSort((s) => {
      if (s.key !== key) {
        // First click on a new column: numbers descend by default, strings ascend.
        const looksNumeric = safeRows.some((r) => typeof r[key] === "number");
        return { key, dir: looksNumeric ? "desc" : "asc" };
      }
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  };

  const renderHeader = (k, align = "right") => {
    const isActive = sort.key === k;
    return (
      <button type="button"
        onClick={() => headerClick(k)}
        className={`inline-flex items-center gap-1 select-none w-full ${align === "right" ? "justify-end" : "justify-start"} text-[10px] uppercase tracking-wider font-semibold transition-colors ${
          isActive ? "text-[#002FA7]" : "text-zinc-500 hover:text-zinc-800"
        }`}
        data-testid={`sort-header-${k}`}
      >
        <span className="whitespace-nowrap">{humanLabel(null, k)}</span>
        <span className="w-3 inline-block text-center text-[9px]">
          {isActive ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    );
  };

  return (
    <div className="rounded-sm border border-zinc-200 bg-white overflow-hidden">
      <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-700 font-semibold">
          {title}
        </div>
        <div className="text-[10px] font-mono text-zinc-400">{rows.length} rows</div>
      </div>
      <div className="overflow-x-auto max-h-[420px]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-zinc-100 sticky top-0">
              <th className="text-left px-4 py-2">
                {renderHeader(dimKey, "left")}
              </th>
              {otherKeys.map((k) => (
                <th key={k} className="text-right px-4 py-2">
                  {renderHeader(k, "right")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.slice(0, 100).map((r, i) => (
              <tr key={`${r[dimKey]}-${i}`}
                className="border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors">
                <td className="px-4 py-2 font-mono text-xs text-zinc-900 max-w-[420px] truncate">
                  {String(r[dimKey] ?? "—")}
                </td>
                {otherKeys.map((k) => (
                  <td key={k}
                    className="text-right px-4 py-2 font-mono text-xs text-zinc-700 whitespace-nowrap">
                    {formatMetric(k, r[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 100 && (
        <div className="px-4 py-2 text-[10px] font-mono text-zinc-400 border-t border-zinc-100 bg-zinc-50">
          Showing first 100 of {rows.length} rows · full list saved on import
        </div>
      )}
    </div>
  );
}

/** The full report-style renderer for a preview payload. */
export function ReportView({ service, data }) {
  const totals = data?.totals || {};
  const breakdowns = data?.breakdowns || {};
  const totalKeys = Object.keys(totals);
  const breakdownKeys = Object.keys(breakdowns);
  const noData = totalKeys.length === 0 && breakdownKeys.length === 0;

  if (noData) {
    return (
      <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-6 text-center text-sm text-zinc-500"
        data-testid="report-empty">
        No data returned for the selected fields and date range.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-view">
      {totalKeys.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-700 font-semibold mb-3">
            Key metrics
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {totalKeys.map((k) => (
              <KpiTile
                key={k}
                label={humanLabel(service, k)}
                value={k}
                formatted={formatMetric(k, totals[k])}
                testId={`kpi-${k}`}
              />
            ))}
          </div>
        </div>
      )}
      {breakdownKeys.length > 0 && (
        <div className="space-y-4">
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-700 font-semibold">
            Breakdowns
          </div>
          {breakdownKeys.map((bk) => (
            <BreakdownTable
              key={bk}
              title={humanLabel(service, bk)}
              rows={breakdowns[bk]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Per-feed data preview page.
 *
 * Lets the admin pick exactly which fields they want pulled from the assigned
 * Google resource, choose a date range (defaults to current month), and run
 * a preview that will (Phase 2) hit the live API and show results.
 */
